import { IAttrValue, IRowData } from "@multiweb/types/contracts";
import { FrameContext } from "../../../UI/UI/UI/UIManagerInterfaces";
import { AwaitableStandardEvent, Event } from "../Events";
import { IComparable } from "../IComparable";
import { CancellationToken } from "../Promises/CancellationTokenSource";

/** Typ hodnoty parametru podmínky */
export type ConditionParam = IComparable | boolean | number | string | null;

/** Typ kolekce s hodnotami pro parametry podmínky */
export type ConditionParams = readonly ConditionParam[] | null | undefined;

/**
 * Interface pro DataSource
 */
export interface IDataSource extends Iterable<IRowData> {
    //#region Events

    /**
     * Vraci radek a zmeneny atribut
     */
    readonly attributesChanged: Event<IDataSource, { key: unknown; instanceId?: string; isChanged: boolean; values: Map<string, IAttrValue> }>;

    /**
     * Vraci startindex pro cteni dat
     */
    readonly dataLoaded: Event<IDataSource, IDataSourceLoadInfo>;

    /**
     * Pokud je vracena pri cteni dat chyba je vyvolana tato udalost
     */
    readonly error: Event<IDataSource, unknown>;

    /**
     * Pokud je spuštěno načítání dat
     */
    readonly loadingStarted: Event<IDataSource, Promise<boolean>>;

    /**
    * Pokud je ukončeno načítání dat
    */
    readonly loadingEnded: Event<IDataSource, IDataSourceLoadInfoBase>;

    /**
     * Pokud je zrušeno načítání dat
     */
    readonly loadingCancelled: Event<IDataSource, IDataSourceLoadInfoBase | null>;
    //#endregion Events

    //#region Fields
    /**
     * Nazev atributu pouziteho jako ID
     */
    readonly dataKey: string;

    /**
     * Název datové třídy, se kterou datasource pracuje
     */
    readonly className: string;

    /**
     * Property slouzici k definici trideni
     */
    orderBy: IOrderBy[];

    /**
     * Nastavuje či vrací podmínku, která byla použita pro načtení dat
     */
    condition: string;

    /**
     * Smaže dočtené záznamy v datasoursu
     */
    clearData(): void;

    /**
     * Vrací podmínku potřebnou pro načtení dat
     */
    getOriginalCondition(): string;

    /**
     * Nastavuje či vrací vstupy do podmínky, které byly použity pro načtení dat
     */
    conditionParams?: ConditionParams;
    //#endregion Fields

    //#region Methods
    /**
     * Vrací pole atributů, které datasource čte
     */
    getAttrs(): string[];

    /** Vrací pole atributů, jejichž hodnoty se nemají ořezávat */
    getFullLengthAttrs(): string[];

    /**
     * Nastavi atributy, ktere chci docist
     * @param attrs Seznam atributu
     * @param fullLengthAttributes Seznam atributu, jejichž hodnoty se nemají ořezávat
     */
    setAttrs(attrs: string[], fullLengthAttributes?: string[]): void;

    /**
     * Nastaví atributy, ktere chci dočíst a dočte data, pokud přibyl nějaký nový atribut
     * @param attrs seznam atributu
     */
    setAttrsAndLoad(attrs: string[], cancellationToken?: CancellationToken): Promise<Iterable<IRowData>>;

    /**
     * Nahraje data od zacatku
     * @param keepSelectedRecords Příznak, zda mají po dočtení zůstat dříve označené záznamy
     */
    loadNewData(keepSelectedRecords?: boolean, cancellationToken?: CancellationToken): Promise<Iterable<IRowData>>;

    /**
     * Vyvola dotaz na dalsi varku dat
     * @param keepSelectedRecords Příznak, zda mají po dočtení zůstat dříve označené záznamy
     */
    getNextData(startIndex?: number, count?: number, keepSelectedRecords?: boolean, cancellationToken?: CancellationToken): Promise<Iterable<IRowData>>;

    /**
     * Metoda aktualizuje data datasource
     * @param fetchCount Volitelný počet záznamů, které se mají obnovit, pokud není vyplněný, tak refresh řídí standardním fetchCountem
     */
    refreshData(fetchCount?: number, manualRefresh?: boolean, cancellationToken?: CancellationToken): Promise<Iterable<IRowData>>;

    /**
     * Pokud zrovna probíhá načítání nebo reload dat, tak vrací promise, pomocí kterého jde na dočítání počkat, jinak vrací dokončený promise
     * @param ct Token umožňující zrušení procesu
     */
    waitForReload(ct?: CancellationToken): Promise<void>;
    /**
     * Nastavuje filtrační podmínku pro dataSource
     * @param condition Podmínka
     */
    setFilterCondition(condition: string): void;
    /**
     * Vrací podmínku vytvořenou filtrem
     */
    getFilterCondition(): string;
    /**
     * Vrací true, pokud je datasource filtrován.
     */
    hasFilterCondition: boolean;

    /**
    * Metoda pro dočtení jednoho určitého záznamu
    * @param dataKey Datový klíč (většinou ID) k načtení
    * @param selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    loadSpecificRecord(dataKey: string, selectLoaded: boolean, cancellationToken?: CancellationToken): Promise<IRowData>;

    /**
    * Metoda pro dočtení určitých záznamů
    * @param dataKeys Pole datových klíčů (většinou ID) k načtení
    * @param selectLoaded Příznak, zda se dočtené záznamy mají označit
    */
    loadSpecificRecords(dataKeys: string[], selectLoaded: boolean, cancellationToken?: CancellationToken): Promise<IRowData[]>;

    /** Vrací počet zadaných záznamů pro aktuálně nastavený filtr */
    count(): Promise<IRecordsInfo>;
    //#endregion Methods
}

/**
 * Informace o počtu záznamů
 * @includeToDoc
 */
export interface IRecordsInfo {
    /** Počet všech záznamů */
    totalCount: number;
    /** Počet záznamů odpovídajících filtru */
    filteredCount?: number;
    /** Informace o tom, zda je aplikován filtr */
    isFiltered: boolean;
}

/**
 * Rozhraní pro DataSource, který používá funkci distinct
 * @generateInstanceClass
 */
export interface IUseDistinctDataSource extends IDataSource {
    /**
     * Nastavuje či vrací zda datasource umožňuje pouze neduplicitní záznamy
     */
    useDistinct: boolean;
}

export interface IDataSourceExtended extends IDataSource {
    /**
     * Nastaví dodatečnou podmínku
     * @param conditionId Identifikátor dodatečné podmínky
     * @param condition Podmínka
     * @param params Nepovinné parametry podmínky
     */
    setAdditionalCondition(conditionId: string, condition: string, params?: ConditionParams): void;

    /**
     * Nastaví parametry pro dodatečnou podmínku
     * @param conditionId Identifikátor dodatečné podmínky
     * @param condition Podmínka
     * @returns {}
     */
    setAdditionalConditionParams(conditionId: string, params: ConditionParams): void;

    /**
     * Vrátí zda podmínka existuje, případně zda existuje alespoň jedna libovolná podmínka při prázdném vstupu
     * @param conditionId Identifikátor dodatečné podmínky
     */
    hasAdditionalCondition(conditionId?: string): boolean;

    /**
     * Odebere dodatečnou podmínku
     * @param conditionId Identifikátor dodatečné podmínky
     */
    removeAdditionalCondition(conditionId: string): void;

    /** Zruší všechny dodatečné podmínky */
    clearAdditionalConditions(): void;
}

/**
 * Interface pro editovatelny DataSource
 */
export interface IEditableDataSource extends IDataSource {
    //#region Fields
    /**
     * Property urcujici jestli se jedna o editovatelny DataSource
     */
    readonly isEditable: boolean;
    /**
     * Vrací počet editovaných objektů.
     */
    readonly editedObjectsCount: number;
    /**
     * Klic, ktery v IRow obsahuje opravneni pro jednotlive polozky
     */
    readonly permissionKey: string;
    //#endregion Fields

    //#region Events
    /**
     * Vraci informaci o zruseni zmen
     */
    readonly changesCancelled: Event<IEditableDataSource, any>;

    /**
     * Vraci novy objekt T
     */
    readonly newCreated: AwaitableStandardEvent<IEditableDataSource, Readonly<IRowData>>;

    /**
     * Vraci mazany radek
     * Parametr je key
     */
    readonly deleted: Event<IEditableDataSource, { key: string; maintainSelection: boolean }>;

    /**
     * Vraci pri ulozeni novy key zaznamu
     */
    readonly saved: Event<IEditableDataSource, Readonly<ISaveRowEventArgs>>;

    /** Vrací záznamy, u kterých právě začalo ukládání. Umožňuje před uložením provést dodatečné změny/kontroly */
    readonly beforeSave: AwaitableStandardEvent<IEditableDataSource, Readonly<{ objects: ReadonlyArray<any> }>>;

    /**
     * Vraci ulozene zaznamy po skonceni ukladani
     */
    readonly afterSaved: Event<IEditableDataSource, Readonly<{ saved: ReadonlyArray<string> }>>;

    /**
     * Událost vyvolaná při vyhodnocení práv
     */
    readonly permissionsChanged: Event<IEditableDataSource, Readonly<{ key: any; permissions: ReadonlyArray<IPermissionData> }>>;

    /**
     * Vraci obnoveny radek
     */
    readonly rowRefreshed: Event<IEditableDataSource, Readonly<IRowData>>;
    //#endregion Events

    //#region Methods
    /**
     * Vrati hodnoty zadanych atributu
     */
    getValues(key: any, attribute?: string[]): Promise<IRowData | null>;

    /**
     * Nastavi atributy, ktere chci docist
     * @param attrs seznam atributu
     */
    setValues(key: any, values: IRowData): Promise<void>;

    /**
     * Zruší všechny změny provedené nad uvedeným záznamem nebo nad všemi
     * @param key Klíč záznamu, nad kterým se mají zrušit změny
     * @param forceRefreshCancelledRecord Znovu načte záznam, na kterém byly zrušeny změny
     */
    cancelChanges(key?: string, forceRefreshCancelledRecord?: boolean): Promise<void>;

    /**
     * Vrátí oprávnění pro aktuální radek
     * @param key
     */
    getRowPermission(key: string): Promise<Array<IPermissionData>>;
    //#endregion Methods
}

export interface IPermissionData {
    attrName: string;
    enabled: boolean;
    visible: boolean;
    required: boolean;
}

export interface ISaveRowEventArgs {
    key: any;
    newKey: any;
}

export interface IOrderBy {
    attributeName: string;
    order: OrderType;
    additionalOrdering?: AdditionalOrdering;
}

export enum AdditionalOrdering {
    linguistic = "LINGUISTIC",
}
export enum OrderType {
    ascending = "ascending",
    descending = "descending",
}

export interface IDBDataSourceParams {
    /** Třída, jejíž záznamy se mají načítat. */
    className: string;
    /** Atributy (sloupečky), které se mají načíst. Při neuvedení se načítá jen ID záznamů. */
    attributes?: string[];
    /** Podmínka omezující čtení dat. */
    condition?: string;
    /** Parametry pro podmínku. */
    conditionParams?: ConditionParams;
    /** Řazení dat v dotazu */
    orderBy?: IOrderBy[];
    /** Maximální délka textových atributů */
    dataLength?: number;
    /** Zda se mají číst verzovaná data. */
    readVersioned?: boolean;
    /**
     * Velikost dávky, o jaké se mají postupně načítat data.
     * Při neuvedení se použije výchozí hodnota.
     * Hodnota -1 znamená všechny existující záznamy.
     */
    batchSize?: number;
    /** Maximální počet záznamů, které se mají načíst. */
    maxRecordCount?: number;
    /** Kontext framu */
    frameContext: FrameContext;
    /** Třídy kompozitních atributů. (Středníkem oddělené hodnoty Název_Atributu=Název_třídy)*/
    composites?: string;
}

/**
 * Obsahuje základní informace o načtených datech.
 * @generateInstanceClass
 */
export interface IDataSourceLoadInfoBase {
    /** True, pokud jsou data filtrována */
    isFiltered: boolean;

    /** Vyhledávaný text ve fulltextovém vyhledávání */
    fulltextSearchString?: string;

    /** True, v případě, že data source nenačetl žádná data*/
    emptyData?: boolean;

    /** Token řídící zrušení načítání dat */
    cancellationToken?: CancellationToken;
}

export class IDataSourceLoadInfoBase {
    private static _properties = ["isFiltered", "emptyData"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}

/**
 * Obsahuje informace o načtených datech.
 * @generateInstanceClass
 */
export interface IDataSourceLoadInfo extends IDataSourceLoadInfoBase {
    startIndex: number;
    dataGenerator: () => Iterable<IRowData>;
    lastBatch: boolean;
    refreshedData: boolean;
    /**
     * Příznak, zda se mají zachovat dříve načtená data, která nejsou v této dávce
     */
    keepExisting?: boolean;
    /**
     * Příznak, zda se mají záznamy označit
     */
    selectLoaded?: boolean;
    /**
     * Příznak, zda mají po dočtení zůstat dříve označené záznamy
     */
    keepSelectedRecords?: boolean;
    /**
     * Dodatečná data předaná konkrétním DS
     */
    additionalData?: any;
}

export class IDataSourceLoadInfo {
    private static _properties = ["startIndex", "lastBatch", "refreshedData"];
    private static _methods = ["dataGenerator"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IDataSourceLoadInfoBase;
    }
}

export class IDataSource {
    private static _properties = ["attributesChanged", "dataLoaded", "error", "loadingStarted", "loadingCancelled", "dataKey", "orderBy", "condition", "conditionParams", "hasFilterCondition"];
    private static _methods = ["getAttrs", "setAttrs", "setAttrsAndLoad", "getNextData", "refreshData", "waitForReload", "loadNewData", "setFilterCondition", "loadSpecificRecord", "loadSpecificRecords"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export class IDataSourceExtended {
    private static _properties: string[] = [];
    private static _methods = ["setAdditionalCondition", "setAdditionalConditionParams", "hasAdditionalCondition", "removeAdditionalCondition", "clearAdditionalConditions"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") &&
            instance instanceof IDataSource;
    }
}

export class IUseDistinctDataSource {
    private static _properties = ["useDistinct"];
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) && instance instanceof IDataSource;
    }
}

export class IEditableDataSource {
    private static _properties: (keyof IEditableDataSource)[] = ["editedObjectsCount", "isEditable", "permissionKey", "changesCancelled", "newCreated", "deleted", "saved", "afterSaved", "rowRefreshed", "beforeSave"];
    private static _methods: (keyof IEditableDataSource)[] = ["getValues", "setValues", "getRowPermission"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IDataSource;
    }
}

/**
 * Základní struktura pro klíč hodnota
 * @includeToDoc
 */
export interface IKeyValueItem {
    key: string;
    value: string;
}

export class IKeyValueItem {
    private static _properties: string[] = ["key", "value"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return typeof (instance) === "object" && instance != null && !this._properties.some((value) => !Reflect.has(instance, value));
    }
}
/**
 * Datasource s podporou multieditace
 */
export interface IMEDataSource extends IEditableDataSource {
    /*
     * Příznak, jestli byla provedena alespoň jedna hromadná změna
     */
    readonly isMultiEditing: boolean;
    /**
     * Aktualizuje výběr objektů na seznamu
     * @param isSelectedAll Příznak zda je aktivní volba vybrat vše.
     * @param lastId ID posledního kliknutého řádku.
     * @param selectedRowsIds Seznam ID aktuálně vybraných záznamů.
     */
    updateSelectedGroup(isSelectedAll?: boolean, lastId?: string, selectedRowsIds?: string[]): void;

    /**
     * Událost, která se vyvolá v případě změny atributu isMultiEditing;
     */
    readonly isMultiEditingChanged: Event<IMEDataSource, boolean>;

}
export class IMEDataSource {
    private static _properties: (keyof IMEDataSource)[] = ["isMultiEditing", "isMultiEditingChanged"];
    private static _methods: (keyof IMEDataSource)[] = ["updateSelectedGroup"];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function") && instance instanceof IEditableDataSource;
    }
}
